/**
 * Link an order to a chat message — what the client posts to
 * `POST /api/chat/link-order` after a chat checkout completes. The server
 * verifies that the authenticated user (1) owns the order and (2) is a
 * participant in the canonical conversation, then writes the order id onto
 * the message row.
 *
 * @see app/api/chat/link-order/route.ts — handler
 * @see lib/chat/link-order.ts linkOrderToMessage — caller
 */

import { z } from "zod";
import { pinned } from "./pin";

export interface LinkOrderToMessageRequest {
  /**
   * Chat room name as provided by the client. The server canonicalises it
   * (split by `":"`, sort) before resolving the conversation, so case /
   * participant-order from the client side does not have to be canonical.
   */
  roomName: string;
  /** `messages.id` to attach the order to. */
  messageId: string;
  /** Human-readable `orders.order_number` (the server resolves it to `orders.id`). */
  orderNumber: string;
}

// Runtime parse — Zod schema (boundary). `pinned<LinkOrderToMessageRequest>()`
// is the compile-time schema↔interface pin; see `./pin.ts`.
export const LinkOrderToMessageRequestSchema = pinned<LinkOrderToMessageRequest>()(
  z.object({
    roomName: z.string().min(1),
    messageId: z.string().min(1),
    orderNumber: z.string().min(1),
  }),
);
